import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

const dipRelated: RL[] = [
  {
    title: "Is SAINT® TMS Safe?",
    href: "/learn/is-saint-tms-safe",
    description:
      "Side effects, long-term effects, and who shouldn't have SAINT®",
  },
  {
    title: "What Happens After SAINT® TMS?",
    href: "/learn/what-happens-after-saint-tms",
    description:
      "Recovery, follow-up, and what to expect after the treatment week",
  },
  {
    title: "What if SAINT® Doesn't Work?",
    href: "/learn/what-if-saint-doesnt-work",
    description: "Next steps if SAINT® doesn't produce enough relief",
  },
  {
    title: "What Does SAINT® TMS Feel Like?",
    href: "/learn/what-does-saint-tms-feel-like",
    description: "What the stimulation feels like during a session",
  },
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "What Is Treatment-Resistant Depression?",
    href: "/learn/what-is-treatment-resistant-depression",
    description: "Definition, causes, and treatment options",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "Is it normal to feel worse during TMS?",
    answer:
      "Some people notice a temporary dip in mood during or shortly after TMS treatment. This is often called a 'TMS dip.' It is not the same as the treatment not working, and it usually passes within days. The 2022 SAINT® randomized trial did not report worsening as a common outcome (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. If your mood drops, tell the treatment team right away.",
  },
  {
    question: "How long does the TMS dip last?",
    answer:
      "A TMS dip — when it happens — typically lasts a few days. It may appear during the treatment week or in the first week or two after treatment ends. Most people who experience it find that it resolves on its own as the brain adjusts. If a low mood persists beyond a couple of weeks or gets worse, contact your treatment team. If you are in crisis, call or text 988.",
  },
  {
    question: "Can TMS make anxiety worse?",
    answer:
      "Some people report feeling more anxious during TMS treatment, though this is not a well-established side effect in the trial data. SAINT® is FDA-cleared for major depressive disorder — not for anxiety disorders. If you notice increased anxiety during treatment, tell the team so they can adjust the plan. Any change in mood, including anxiety, should be reported and monitored.",
  },
  {
    question: "Does a dip mean SAINT® isn't working?",
    answer:
      "Not necessarily. A temporary dip does not mean the treatment has failed. Some people who feel worse briefly go on to improve. The treatment team tracks your mood throughout the week and after, so they can tell the difference between a passing dip and a pattern that needs a different approach. If you are concerned, call us at 954-827-9793.",
  },
  {
    question: "What should I do if I feel worse after SAINT®?",
    answer:
      "Call your treatment team right away. Do not wait for your next scheduled follow-up. The team can review your symptoms, adjust the plan, or bring in a psychiatric evaluation. If you are having thoughts of suicide or are in crisis, call or text 988 immediately. The 988 Suicide and Crisis Lifeline is available 24/7, free and confidential.",
  },
];

export const pageCanSaintMakeDepressionWorse = makeArticle({
  slug: "can-saint-make-depression-worse",
  title: "Can SAINT® TMS Make Depression Worse? The 'TMS Dip' Explained",
  description:
    "Some people feel worse briefly during or after TMS. What the 'TMS dip' is, how common it is with accelerated protocols, and what to do.",
  canonical: "/learn/can-saint-make-depression-worse",
  h1: "Can SAINT® Make Depression Worse? Understanding the 'TMS Dip'",
  breadcrumbLabel: "Can SAINT® Make Depression Worse?",
  shortAnswer:
    "Some people notice a temporary dip in mood during or shortly after TMS treatment — often called a 'TMS dip.' It is usually mild and passes within days. The 2022 SAINT® randomized trial did not report worsening as a common outcome (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. As with any depression treatment, there is a risk of worsening symptoms or suicidal thoughts. If you are in crisis, call or text 988.",
  keyTakeaways: [
    "A 'TMS dip' is a temporary, usually mild worsening of mood during or shortly after treatment.",
    "The 2022 SAINT® trial did not report worsening as a common outcome (Cole et al., 2022). Individual results may vary.",
    "A dip does not mean the treatment has failed — most people who experience it improve.",
    "Tell the treatment team right away if your mood drops; they can adjust the plan.",
    "SAINT® is FDA-cleared for major depressive disorder in adults, not for anxiety, OCD, PTSD, or bipolar disorder.",
    "If you are in crisis, call or text 988.",
  ],
  sections: [
    {
      heading: "Quick Answer: Can SAINT® Make Depression Worse?",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is FDA-cleared for major depressive disorder (MDD) in adults whose depression has not improved enough on prior antidepressant medication. Some people notice a temporary dip in mood during or shortly after treatment — often called a 'TMS dip.' This is usually mild and passes within days. The 2022 SAINT® randomized trial did not report worsening of depression as a common outcome (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary.",
        "As with any depression treatment, there is a risk of worsening symptoms or suicidal thoughts. The treatment team monitors you throughout the week and after. If you notice your mood getting worse, tell the team right away. If you are in crisis, call or text 988. The 988 Suicide and Crisis Lifeline is available 24/7, free and confidential.",
        "If you are in crisis or having thoughts of suicide, call or text 988 right now. The 988 Suicide and Crisis Lifeline is available 24/7.",
      ],
    },
    {
      heading: "What the 'TMS Dip' Is",
      paragraphs: [
        "The 'TMS dip' is a term patients and clinicians use to describe a temporary worsening of mood that some people experience during or shortly after TMS treatment. It is not an official medical diagnosis, and it is not the same as the treatment not working. Think of it as an adjustment period — the brain is responding to repeated stimulation, and some people feel worse before they feel better.",
        "The dip can show up as a drop in mood, increased tearfulness, irritability, fatigue, or a sense that things are not improving fast enough. It is usually mild. Some people do not notice it at all. Others feel it for a day or two during the treatment week or in the first week or two after treatment ends.",
        "It is important to distinguish a dip from a genuine worsening that needs attention. A dip is temporary and mild. If your mood is dropping sharply, lasting more than a couple of weeks, or includes thoughts of suicide, that is not a dip — call your treatment team and, if you are in crisis, call or text 988.",
        "Learn more about [[how SAINT® works|/saint]] or [[what treatment-resistant depression is|/learn/what-is-treatment-resistant-depression]].",
      ],
    },
    {
      heading: "What the Trials Reported",
      paragraphs: [
        "In the 2022 SAINT® randomized controlled trial, the researchers tracked adverse events and symptom changes throughout treatment and follow-up. Worsening of depression was not reported as a common outcome. The most commonly reported side effects were headache and scalp discomfort, which were mild to moderate and temporary (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary.",
        "The trial used standardized mood scales to track symptoms at multiple points during the five-day treatment week and during follow-up. This means the research team was actively monitoring for worsening — and did not flag it as a frequent problem in this study.",
        "That said, the published SAINT® trials are relatively small compared to large multi-center medication trials. Rare effects may not show up until more people have been treated and followed over longer periods. Your psychiatrist can discuss what the current evidence does and does not cover for your individual situation.",
        "For a full breakdown of side effects and risks, see [[Is SAINT® TMS Safe?|/learn/is-saint-tms-safe]].",
      ],
    },
    {
      heading:
        "Day by Day: What's Normal in the 5-Day Week and the 30 Days After",
      paragraphs: [
        "During the five-day SAINT® week, you receive 10 sessions per day with breaks between sessions. It is normal to feel tired — you are spending most of the day at the clinic. Headache and scalp discomfort are the most common physical side effects and usually ease over the first day or two.",
        "Mood-wise, some people feel better quickly — even within the first day or two. Others do not notice a change until later in the week or after the week ends. A temporary dip in mood, if it happens, is most likely to appear during the week or in the first week or two after treatment.",
        "In the 30 days after SAINT®, the treatment team follows up to track your response. Some people continue to improve. Others level off. A small number may need additional treatment or a different approach. The team checks in so they can tell the difference between a passing dip, a plateau, and a need to adjust the plan.",
        "If at any point your mood drops sharply, you feel hopeless, or you have thoughts of suicide, call your treatment team right away. If you are in crisis, call or text 988. Do not wait for your next scheduled follow-up.",
      ],
    },
    {
      heading: "When to Call Us — and 988",
      paragraphs: [
        "Call your treatment team at 954-827-9793 if you notice any of the following during or after SAINT®:",
        "A sharp drop in mood that does not lift within a day or two. Thoughts of suicide or self-harm. Feeling hopeless or that things will never get better. New symptoms you did not have before treatment. Anxiety that is new or significantly worse. Any change that concerns you.",
        "If you are in crisis or having thoughts of suicide, call or text 988 immediately. The 988 Suicide and Crisis Lifeline is available 24/7, free and confidential. You can also text 988. If you are in immediate danger, call 911.",
        "You do not need to figure out whether what you are feeling is a 'dip' or something more serious. Call and let the clinical team assess it. That is what follow-up is for. [[Check your eligibility|/am-i-a-candidate-screener]] or [[verify your insurance|/check-insurance-options]] if you are considering SAINT®.",
      ],
    },
    {
      heading: "The TMS Dip: What's Normal and When to Call",
      table: {
        headers: [
          "Timeframe",
          "What some people notice",
          "What's typical",
          "When to call",
        ],
        rows: [
          [
            "Days 1–2 of treatment",
            "Headache, scalp discomfort, fatigue; possible mild mood dip",
            "Physical side effects ease; mood may fluctuate",
            "Call if headache is severe or mood drops sharply",
          ],
          [
            "Days 3–5 of treatment",
            "Tiredness from the schedule; some notice early mood improvement",
            "Most people tolerate the week well; mood may start to lift",
            "Call if mood worsens or new symptoms appear",
          ],
          [
            "First week after treatment",
            "Possible 'TMS dip' — temporary, mild mood drop",
            "Dip usually passes within days; follow-up scheduled",
            "Call if dip lasts more than a few days or worsens",
          ],
          [
            "Weeks 2–4 after treatment",
            "Mood stabilizing; some continue to improve",
            "Follow-up visits track response; team adjusts if needed",
            "Call if mood drops, or if thoughts of suicide arise",
          ],
          [
            "Anytime",
            "Thoughts of suicide, self-harm, or hopelessness",
            "Not a dip — needs immediate attention",
            "Call or text 988 immediately; call 911 if in danger",
          ],
        ],
      },
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: dipRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "October 2026",
    lastReviewed: "October 2026",
    lastUpdated: "October 2026",
  },
  toc: [
    {
      label: "Quick Answer",
      href: "#quick-answer-can-saint-make-depression-worse",
    },
    {
      label: "What the 'TMS Dip' Is",
      href: "#what-the-tms-dip-is",
    },
    {
      label: "What the Trials Reported",
      href: "#what-the-trials-reported",
    },
    {
      label: "Day by Day: 5-Day Week & 30 Days After",
      href: "#day-by-day-whats-normal-in-the-5-day-week-and-the-30-days-after",
    },
    {
      label: "When to Call Us — and 988",
      href: "#when-to-call-us-and-988",
    },
    {
      label: "TMS Dip Table",
      href: "#the-tms-dip-whats-normal-and-when-to-call",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
